// Two-finger pinch-to-zoom gesture state, extracted from ViewportController
// to keep that file under the 200-line cap. Zoom factor = currentDistance /
// startDistance off the pinch-start zoom; the world point that sat under the
// pinch midpoint when the gesture began is kept under the fingers by
// re-deriving pan from it every frame.

import type { Viewport } from '../render/Viewport.js';

export interface Point {
  readonly x: number;
  readonly y: number;
}

interface PinchState {
  readonly id1: number;
  readonly id2: number;
  readonly startDist: number;
  readonly startZoom: number;
  readonly startMidWorld: Point;
}

export class PinchGesture {
  private state: PinchState | null = null;

  isActive(): boolean {
    return this.state !== null;
  }

  /** Ids of the two pointers this gesture is tracking, or null if inactive. */
  pointerIds(): readonly [number, number] | null {
    return this.state ? [this.state.id1, this.state.id2] : null;
  }

  start(viewport: Viewport, id1: number, id2: number, p1: Point, p2: Point): void {
    const mid: Point = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1;
    this.state = {
      id1,
      id2,
      startDist: dist,
      startZoom: viewport.getState().zoom,
      startMidWorld: viewport.screenToWorld(mid),
    };
  }

  update(viewport: Viewport, p1: Point, p2: Point): void {
    const pinch = this.state;
    if (!pinch) return;
    const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1;
    const targetZoom = pinch.startZoom * (dist / pinch.startDist);
    const mid: Point = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    const state = viewport.getState();
    viewport.resetTo(state.panX, state.panY, targetZoom);
    const clampedZoom = viewport.getState().zoom;
    viewport.resetTo(
      mid.x - pinch.startMidWorld.x * clampedZoom,
      mid.y - pinch.startMidWorld.y * clampedZoom,
      clampedZoom,
    );
  }

  clear(): void {
    this.state = null;
  }
}
